import { Routes, Route, Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { lazy, Suspense, useState, useEffect, useRef } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth, db, functions, storage } from '@/lib/firebase';
import { FirebaseProvider } from '@mesterplan/shared-firebase';
import { useOptionalMesterplanApi } from '@mesterplan/web-hooks';
import type { MesterplanApi } from '@mesterplan/api-client';
import { CompanyProvider } from '@/contexts/CompanyContext';
import { NetUserProvider } from '@/contexts/NetUserContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { idpEnabled } from '@/lib/idp';
import { isGuestPath } from '@/lib/guestPaths';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { settingsRoutes } from '@/routes/settingsRoutes';
import { useAuth } from '@mesterplan/auth-react';
import AdminLayout from '@/components/layout/AdminLayout';
import SupplierLayout from '@/components/layout/SupplierLayout';
import { resolveShellRedirect, supplierOrgGate } from '@/components/layout/supplierNavigation';
const SupplierSellers = lazy(() => import('@/pages/supplier/SupplierSellers'));
const SupplierOverview = lazy(() => import('@/pages/supplier/SupplierOverview'));
const SupplierCatalog = lazy(() => import('@/pages/supplier/SupplierCatalog'));
const SupplierCustomers = lazy(() => import('@/pages/supplier/SupplierCustomers'));
const SupplierGoals = lazy(() => import('@/pages/supplier/SupplierGoals'));
const SupplierReferrals = lazy(() => import('@/pages/supplier/SupplierReferrals'));
const AcceptSupplierInvitation = lazy(() => import('@/pages/supplier/AcceptSupplierInvitation'));
import { useCompany } from '@/contexts/CompanyContext';
import { useActiveOrgId, useOrgsStatus } from '@/contexts/NetClientsContext';
import { Button } from '@/components/ui/button';
const PlatformAdminApp = lazy(() =>
  import('@/pages/admin/PlatformAdminApp').then((m) => ({ default: m.PlatformAdminApp })),
);
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Users = lazy(() => import('@/pages/Users'));
const UserDetails = lazy(() => import('@/pages/UserDetails'));
const Projects = lazy(() => import('@/pages/Projects'));
const ProjectDetails = lazy(() => import('@/pages/ProjectDetails'));
const Customers = lazy(() => import('@/pages/Customers'));
const CustomerDetails = lazy(() => import('@/pages/CustomerDetails'));
const Quotes = lazy(() => import('@/pages/Quotes'));
const QuoteDetails = lazy(() => import('@/pages/QuoteDetails'));
const Invoices = lazy(() => import('@/pages/Invoices'));
const InvoiceDetails = lazy(() => import('@/pages/InvoiceDetails'));
const NetInvoiceDetails = lazy(() => import('@/pages/NetInvoiceDetails'));
const Planning = lazy(() => import('@/pages/Planning'));
const Reports = lazy(() => import('@/pages/Reports'));
const Logs = lazy(() => import('@/pages/Logs'));
const Insights = lazy(() => import('@/pages/Insights'));
const ProfileSettings = lazy(() => import('@/pages/ProfileSettings'));
const PermissionSettings = lazy(() => import('@/pages/PermissionSettings'));
const MaterialMarkupSettings = lazy(() => import('@/pages/settings/MaterialMarkupSettings'));
const CustomerScoreSettings = lazy(() => import('@/pages/settings/CustomerScoreSettings'));
const ServiceOrderSettings = lazy(() => import('@/pages/settings/ServiceOrderSettings'));
const EconomySettings = lazy(() => import('@/pages/settings/EconomySettings'));
const BonusSchemeSettings = lazy(() => import('@/pages/settings/BonusSchemeSettings'));
const ProductGroupSettings = lazy(() => import('@/pages/settings/ProductGroupSettings'));
const TimeTypeSettings = lazy(() => import('@/pages/settings/TimeTypeSettings'));
const CommunicationSettings = lazy(() => import('@/pages/settings/CommunicationSettings'));
const QuoteDefaultsSettings = lazy(() => import('@/pages/settings/QuoteDefaultsSettings'));
const UnifiedWalkthroughTemplates = lazy(() => import('@/pages/settings/UnifiedWalkthroughTemplates'));
const UnifiedWalkthroughTemplateEditor = lazy(() => import('@/pages/settings/UnifiedWalkthroughTemplateEditor'));
const CalculationTemplateSettings = lazy(() => import('@/pages/settings/CalculationTemplateSettings'));
const CalculationTemplateEditor = lazy(() => import('@/pages/settings/CalculationTemplateEditor'));
const IntegrationConnect = lazy(() => import('@/pages/IntegrationConnect'));
const TrackrsAttention = lazy(() => import('@/pages/TrackrsAttention'));
import { ActiveOrgFromQuery } from '@/components/ActiveOrgFromQuery';
import { parseConnectParams, rememberConnectParams } from '@/hooks/integrationConnections.utils';
const EfakturaInbox = lazy(() => import('@/pages/EfakturaInbox'));
const Help = lazy(() => import('@/pages/Help'));
const WhatsNew = lazy(() => import('@/pages/WhatsNew'));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));
const Login = lazy(() => import('@/pages/Login'));
const Signup = lazy(() => import('@/pages/Signup'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const PriceLibrary = lazy(() => import('@/pages/PriceLibrary'));
const AiAnalyse = lazy(() => import('@/pages/AiAnalyse'));
import { LegacyAnalysisRedirect } from '@/components/analysis/LegacyAnalysisRedirect';
const Budgettering = lazy(() => import('@/pages/Budgettering'));
const KS = lazy(() => import('@/pages/KS'));
const KLS = lazy(() => import('@/pages/KLS'));
const AcceptInvitation = lazy(() => import('@/pages/AcceptInvitation'));
const RespondToQuote = lazy(() => import('@/pages/RespondToQuote'));
import { PortalProvider } from './contexts/PortalContext';
import PortalProtectedRoute from './components/portal/PortalProtectedRoute';
const PortalLogin = lazy(() => import('./pages/portal/PortalLogin'));
const PortalDashboard = lazy(() => import('./pages/portal/PortalDashboard'));
const PortalConversation = lazy(() => import('./pages/portal/PortalConversation'));
const PortalTidsplan = lazy(() => import('./pages/portal/PortalTidsplan'));
const GuestTimeplan = lazy(() => import('./pages/GuestTimeplan'));
const GuestKS = lazy(() => import('./pages/GuestKS'));
const DefectCompletion = lazy(() => import('./pages/DefectCompletion'));
const DefectBatchList = lazy(() => import('./pages/DefectBatchList'));
const WalkthroughEditor = lazy(() => import('./pages/WalkthroughEditor'));
const RecipientsPage = lazy(() => import('@/pages/RecipientsPage'));
const Assets = lazy(() => import('@/pages/Assets'));
const Properties = lazy(() => import('@/pages/Properties'));
const PropertyDetails = lazy(() => import('@/pages/PropertyDetails'));
const AssetDetails = lazy(() => import('@/pages/AssetDetails'));
import { BrandMark } from '@/components/brand/BrandMark';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { RouteFallback } from '@/components/layout/RouteFallback';

/** The old per-project budget page is gone; its numbers live on the project's Økonomi tab. */
function ProjectBudgetRedirect() {
  const { projectId } = useParams<{ projectId: string }>();
  return <Navigate to={`/projects/${projectId}?tab=okonomi`} replace />;
}

function LoadingScreen({ message = 'Indlæser...' }: { message?: string }) {
  return (
    <ThemeProvider>
      <div className="flex h-screen items-center justify-center bg-surface dark:bg-dark-surface">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted dark:text-dark-muted">{message}</p>
        </div>
      </div>
    </ThemeProvider>
  );
}


/** The org list could not be read: say so and let the user ask again, instead of a spinner that never ends. */
function OrgsLoadFailed({ onRetry }: { onRetry: () => void }) {
  return (
    <ThemeProvider>
      <div className="flex h-screen items-center justify-center bg-surface px-4 dark:bg-dark-surface">
        <div role="alert" className="max-w-[420px]">
          <h1 className="text-[17px] font-semibold text-dark dark:text-dark-text">Kunne ikke hente dine virksomheder</h1>
          <p className="mb-4 mt-1 text-[14px] text-muted dark:text-dark-muted">Tjek forbindelsen og prøv igen. Ingenting er ændret.</p>
          <Button variant="outline" onClick={onRetry}>Prøv igen</Button>
        </div>
      </div>
    </ThemeProvider>
  );
}

/**
 * Chooses the shell for the active org, like /admin short-circuits to PlatformAdminApp:
 * a Supplier-plan org gets its own layout and menu, everything else the company layout.
 * The decision reads the switcher row, so it waits for the list instead of flashing
 * the company shell at an account manager.
 */
function RootLayout() {
  const { isSupplierOrg, isSupplierSession, companies, isLoadingCompanies } = useCompany();
  const activeOrgId = useActiveOrgId();
  const orgs = useOrgsStatus();
  const { pathname } = useLocation();
  const rowKnown = activeOrgId !== null && companies.some((c) => c.id === activeOrgId);
  if (activeOrgId !== null && !rowKnown && isLoadingCompanies) return <LoadingScreen />;
  // A reload on /supplier/... : the active org is restored after the list loads, so wait instead of bouncing to "/".
  const gate = supplierOrgGate({ pathname, activeOrgId, isLoadingCompanies, hasCompanies: companies.length > 0, orgsFailed: orgs.failed });
  if (gate === 'wait') return <LoadingScreen />;
  if (gate === 'retry') return <OrgsLoadFailed onRetry={orgs.retry} />;
  // Stray links never leak company pages into the supplier shell, or admin pages into a session.
  const redirect = resolveShellRedirect({ isSupplierOrg, isSupplierSession, pathname });
  if (redirect) return <Navigate to={redirect} replace />;
  if (isSupplierOrg) return <SupplierLayout />;
  return <AdminLayout />;
}

/** The landing: the supplier overview for a supplier org, the dashboard for everyone else. */
function HomeIndex() {
  const { isSupplierOrg } = useCompany();
  return isSupplierOrg ? <SupplierOverview /> : <Dashboard />;
}

/** E-faktura needs invoices.view; an account manager inside a customer needs creditorInvoices.view (Bilag, read-only). */
function EfakturaRouteGuard() {
  const { isSupplierSession } = useCompany();
  return <ProtectedRoute permission={isSupplierSession ? 'creditorInvoices.view' : 'invoices.view'} />;
}

function AuthenticatedRoutes({ firebaseUser }: { firebaseUser: User | null }) {
  // Inject the .NET API client (when mounted) so shared hooks can route
  // Guid-keyed entities to the API. `null` on the local Firebase-only path.
  // The web .NET clients expose the concrete MesterplanApi; the hook context
  // requires it (the MesterplanApiLike mirror is intentionally too thin).
  const netApi = useOptionalMesterplanApi() as unknown as MesterplanApi | null;
  return (
    <FirebaseProvider auth={auth} db={db} functions={functions} storage={storage} currentUser={firebaseUser} api={netApi}>
      <CompanyProvider>
        <NetUserProvider>
        <ErrorBoundary><Suspense fallback={<LoadingScreen />}>
        <Routes>
          <Route path="/invitations/accept" element={<AcceptInvitation />} />
          <Route path="/supplier-access/accept" element={<AcceptSupplierInvitation />} />
          <Route path="/quotes/respond" element={<RespondToQuote />} />
          <Route path="/defect/complete" element={<DefectCompletion />} />
          <Route path="/defect/batch" element={<DefectBatchList />} />
          <Route path="/recipients" element={<RecipientsPage />} />
          <Route path="/" element={<RootLayout />}>
            <Route index element={<HomeIndex />} />
            {/* Supplier shell pages. Placeholders until the slices that build them
                (customers: PR8, goals/catalog/referrals/settings: later phases). */}
            <Route path="supplier/customers" element={<SupplierCustomers />} />
            <Route path="supplier/goals" element={<SupplierGoals />} />
            <Route path="supplier/catalog" element={<SupplierCatalog />} />
            <Route path="supplier/referrals" element={<SupplierReferrals />} />
            <Route path="supplier/settings" element={<SupplierSellers />} />
            <Route path="profile" element={<UserDetails />} />
            <Route path="profile/settings" element={<ProfileSettings />} />
            <Route element={<ProtectedRoute permission="users.view" allowPartner />}>
              <Route path="users" element={<Users />} />
              <Route path="users/:id" element={<UserDetails />} />
            </Route>
            <Route element={<ProtectedRoute permission="projects.view" allowPartner />}>
              <Route path="projects" element={<Projects />} />
              <Route path="projects/:id" element={<ProjectDetails />} />
              <Route path="projects/:projectId/budget" element={<ProjectBudgetRedirect />} />
            </Route>
            <Route element={<ProtectedRoute permission="customers.view" />}>
              <Route path="customers" element={<Customers />} />
              <Route path="customers/:id" element={<CustomerDetails />} />
            </Route>
            <Route element={<ProtectedRoute permission="properties.view" />}>
              <Route path="properties" element={<Properties />} />
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route path="properties/:id" element={<PropertyDetails />} />
            </Route>
            {/* Assets guard on assets.view, not customers.view. They had been sitting under the
                customer guard, which both locked out users holding assets.view without
                customers.view and let customers.view through to a page it does not grant. */}
            <Route element={<ProtectedRoute permission="assets.view" />}>
              <Route path="assets" element={<Assets />} />
              <Route path="assets/:id" element={<AssetDetails />} />
            </Route>
            <Route element={<ProtectedRoute permission="quotes.view" allowPartner />}>
              <Route path="quotes" element={<Quotes />} />
              <Route path="quotes/:id" element={<QuoteDetails />} />
              <Route path="walkthroughs/:id" element={<WalkthroughEditor />} />
            </Route>
            <Route element={<ProtectedRoute permission="priceLibrary.view" />}>
              <Route path="price-library" element={<PriceLibrary />} />
            </Route>
            <Route element={<ProtectedRoute permission="calculations.edit" />}>
              <Route path="ai-analyse" element={<AiAnalyse />} />
              <Route path="ai-analyse/:engine/:id" element={<AiAnalyse />} />
              {/* The two features used to have a sidebar entry each. Links to them are in
                  customers' mails and browser history, so the old addresses keep working. */}
              <Route path="tegningsanalyser" element={<Navigate to="/ai-analyse" replace />} />
              <Route path="tegningsanalyser/:id" element={<LegacyAnalysisRedirect engine="tegning" />} />
              <Route path="udbudsanalyser" element={<Navigate to="/ai-analyse" replace />} />
              <Route path="udbudsanalyser/:id" element={<LegacyAnalysisRedirect engine="udbud" />} />
            </Route>
            <Route element={<ProtectedRoute permission="invoices.view" />}>
              <Route path="invoices" element={<Invoices />} />
              <Route path="invoices/net/:id" element={<NetInvoiceDetails />} />
              <Route path="invoices/:id" element={<InvoiceDetails />} />
            </Route>
            <Route element={<EfakturaRouteGuard />}>
              <Route path="efaktura" element={<EfakturaInbox />} />
            </Route>
            <Route element={<ProtectedRoute permission="budgettering.view" />}>
              <Route path="budgettering" element={<Budgettering />} />
            </Route>
            <Route element={<ProtectedRoute permission="planning.view" />}>
              <Route path="planning" element={<Planning />} />
            </Route>
            <Route element={<ProtectedRoute permission="reports.view" />}>
              <Route path="reports" element={<Reports />} />
              <Route path="logs" element={<Logs />} />
            </Route>
            <Route element={<ProtectedRoute permission="insights.view" />}>
              <Route path="insights" element={<Insights />} />
            </Route>
            <Route element={<ProtectedRoute permission="quality.view" />}>
              <Route path="ks" element={<KS />} />
              <Route path="kls" element={<KLS />} />
            </Route>
            {settingsRoutes()}
            {/* Acceptor for a handshake another product (Trackrs) started. No
                permission gate on purpose: the page itself explains to a
                non-admin that an administrator must do it, and ProtectedRoute
                still holds it until companies + /me have resolved. */}
            <Route element={<ProtectedRoute allowPartner />}>
              <Route path="integrations/connect" element={<IntegrationConnect />} />
            </Route>
            {/* "Vi har brug for din hjælp her". Trackrs deep-links here with ?org=<id>, so
                the org switch sits OUTSIDE the permission guard — the guard checks the
                active org, and would bounce a link to another company before the page
                mounted. */}
            <Route element={<ActiveOrgFromQuery />}>
              <Route element={<ProtectedRoute permission="integrations.connections.manage" />}>
                <Route path="integrations/trackrs/attention" element={<TrackrsAttention />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute permission="settings.managePermissions" />}>
              <Route path="settings/permissions" element={<PermissionSettings />} />
            </Route>
            <Route element={<ProtectedRoute permission="settings.editCompany" />}>
              <Route path="settings/material-markup" element={<MaterialMarkupSettings />} />
              <Route path="settings/economy" element={<EconomySettings />} />
              <Route path="settings/customer-score" element={<CustomerScoreSettings />} />
              <Route path="settings/service-orders" element={<ServiceOrderSettings />} />
              <Route path="settings/communication" element={<CommunicationSettings />} />
              <Route path="settings/quote-defaults" element={<QuoteDefaultsSettings />} />
              <Route path="settings/product-groups" element={<ProductGroupSettings />} />
              <Route path="settings/timetyper" element={<TimeTypeSettings />} />
              <Route path="settings/unified-walkthrough-templates" element={<UnifiedWalkthroughTemplates />} />
              <Route path="settings/unified-walkthrough-templates/:templateId" element={<UnifiedWalkthroughTemplateEditor />} />
            </Route>
            <Route element={<ProtectedRoute permission="calculations.edit" />}>
              <Route path="settings/kalkulationsskabeloner" element={<CalculationTemplateSettings />} />
              <Route path="settings/kalkulationsskabeloner/:templateId" element={<CalculationTemplateEditor />} />
            </Route>
            <Route element={<ProtectedRoute permission="bonus.manage" />}>
              <Route path="settings/bonus-scheme" element={<BonusSchemeSettings />} />
            </Route>
            <Route path="help" element={<Help />} />
            {/* Nyt i Mesterplan reads the .NET API, which only exists on the IDP path. */}
            {idpEnabled && <Route path="nyheder" element={<WhatsNew />} />}
            <Route path="privacy-policy" element={<PrivacyPolicy />} />
          </Route>
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/signup" element={<Navigate to="/" replace />} />
          <Route path="/auth/action" element={<ResetPassword />} />
          <Route path="/tidsplan/:token" element={<GuestTimeplan />} />
          <Route path="/ks/:token" element={<GuestKS />} />
          <Route path="/portal" element={<PortalProvider><Suspense fallback={<RouteFallback />}><Outlet /></Suspense></PortalProvider>}>
            <Route index element={<PortalLogin />} />
            <Route path="login" element={<PortalLogin />} />
            <Route element={<PortalProtectedRoute />}>
              <Route path="dashboard" element={<PortalDashboard />} />
              <Route path="conversation/:projectId" element={<PortalConversation />} />
              <Route path="tidsplan/:projectId" element={<PortalTidsplan />} />
            </Route>
          </Route>
        </Routes>
        </Suspense></ErrorBoundary>
        </NetUserProvider>
      </CompanyProvider>
    </FirebaseProvider>
  );
}

// Mesterplan-branded login page shown for the IDP auth path. Instead of bouncing
// the visitor straight to the rs-connect hosted login, we serve our own page and
// only start the login flow when they click "Log ind".
//
// Aesthetic: a split-screen "architect's blueprint" — left panel is a deep brand
// gradient with a faint engineering grid and floating glow, right panel is a clean
// glass login card. Staggered page-load reveal via tailwindcss-animate.
const authButtonClass =
  'group mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary-600 to-primary-500 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-primary/30 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-primary/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0 dark:focus-visible:ring-offset-dark-card';

function AuthArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

const MESTERPLAN_API = (import.meta.env.VITE_MESTERPLAN_API_URL ?? '').replace(/\/+$/, '');

/** The page the visitor is on, as the absolute URL the auth endpoints should return to. */
function currentAppUrl(): string {
  const { origin, pathname, search } = window.location;
  return `${origin}${pathname || '/'}${search}`;
}

// Mesterplan-branded landing screen for the IDP auth path.
// Both actions hand off to the IDP: login via the standard OIDC challenge
// (GET /api/auth/login), signup via the register relay
// (GET /api/auth/register?returnUrl=...) that redirects to the branded
// /register page on the IDP with a ReturnUrl carrying the /connect/authorize URL.
// The password is entered on the IDP origin only — tokens never touch the browser.
function IdpLoginScreen() {
  // Faint engineering grid drawn with CSS gradients — the "blueprint" texture.
  const blueprintGrid: React.CSSProperties = {
    backgroundImage:
      'linear-gradient(rgba(255,255,255,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.10) 1px, transparent 1px)',
    backgroundSize: '38px 38px',
  };

  // Hand off to the authorization-code BFF flow. The API challenges the IDP and the
  // visitor enters their credentials on the hosted RsConnect login page. Pass the SPA
  // URL as returnUrl so the post-callback redirect lands back on the app — the API
  // is a different origin in dev (:3000 SPA vs :5285 API), and "/" would otherwise
  // resolve against the API host. The origin is validated server-side against
  // IdP:AllowedBrowserOrigins; the path and query ride along, so a deep link such
  // as /integrations/connect?from=trackrs&connection=… (Trackrs sending an admin
  // here to finish a handshake) survives the login round trip instead of landing
  // the user on the dashboard with the handshake lost.
  const startLogin = () => {
    window.location.assign(`${MESTERPLAN_API}/api/auth/login?returnUrl=${encodeURIComponent(currentAppUrl())}`);
  };

  // Redirect to the BFF register relay which issues an OIDC challenge with register=1,
  // landing the browser on the branded /register page on the IDP. After registration
  // the IDP session is established and the /connect/authorize ReturnUrl completes the
  // flow straight through back to the SPA — at the same URL, for the same reason.
  const startSignup = () => {
    window.location.assign(`${MESTERPLAN_API}/api/auth/register?returnUrl=${encodeURIComponent(currentAppUrl())}`);
  };

  return (
    <ThemeProvider>
      <div className="flex min-h-screen bg-surface dark:bg-dark-surface font-sans">
        {/* ── Brand panel ─────────────────────────────────────────── */}
        <div className="relative hidden w-1/2 overflow-hidden bg-primary-800 lg:flex">
          {/* layered brand gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary-700 via-primary-600 to-primary-900" />
          {/* accent glow blobs */}
          <div className="pointer-events-none absolute -left-24 top-1/4 h-96 w-96 rounded-full bg-secondary/40 blur-[120px]" />
          <div className="pointer-events-none absolute -bottom-32 right-0 h-96 w-96 rounded-full bg-success/30 blur-[130px]" />
          {/* blueprint grid */}
          <div className="absolute inset-0" style={blueprintGrid} />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-900/70 to-transparent" />

          {/* content */}
          <div className="relative z-10 flex w-full flex-col justify-between p-14 text-white">
            <div className="flex items-center gap-3 duration-700 animate-in fade-in slide-in-from-top-4 fill-mode-both">
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-white/15 ring-1 ring-white/25 backdrop-blur">
                <BrandMark variant="mark" className="h-8 w-8 text-white" />
              </div>
              <span className="font-brand text-xl font-extrabold tracking-tight">Mesterplan</span>
            </div>

            <div className="max-w-md">
              <h2
                className="text-4xl font-extrabold leading-tight tracking-tight duration-700 animate-in fade-in slide-in-from-bottom-6 fill-mode-both"
                style={{ animationDelay: '120ms' }}
              >
                Styr på sagen —<br />fra tilbud til faktura.
              </h2>
              <p
                className="mt-5 text-base leading-relaxed text-white/75 duration-700 animate-in fade-in slide-in-from-bottom-6 fill-mode-both"
                style={{ animationDelay: '220ms' }}
              >
                Hele din virksomhed ét sted: kalkulation, projektstyring,
                planlægning og fakturering.
              </p>

              <ul className="mt-9 space-y-3.5">
                {['Tilbud & kalkulation', 'Projektstyring & planlægning', 'Fakturering & e-conomic'].map(
                  (item, i) => (
                    <li
                      key={item}
                      className="flex items-center gap-3 text-[15px] text-white/90 duration-700 animate-in fade-in slide-in-from-left-4 fill-mode-both"
                      style={{ animationDelay: `${320 + i * 90}ms` }}
                    >
                      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-success/25 ring-1 ring-success/40">
                        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 text-success" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      </span>
                      {item}
                    </li>
                  ),
                )}
              </ul>
            </div>

            <p className="text-sm text-white/50 duration-700 animate-in fade-in fill-mode-both" style={{ animationDelay: '700ms' }}>
              © {new Date().getFullYear()} Mesterplan · Bygget til danske mestre
            </p>
          </div>
        </div>

        {/* ── Card panel ──────────────────────────────────────────── */}
        <div className="relative grid flex-1 place-items-center px-6 py-12">
          {/* subtle ambient blob behind the card (visible on mobile too) */}
          <div className="pointer-events-none absolute -top-10 right-0 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />

          <div
            className="relative w-full max-w-sm rounded-2xl border border-bordergray/80 bg-white/90 p-9 shadow-card backdrop-blur-xl duration-700 animate-in fade-in zoom-in-95 fill-mode-both dark:border-dark-border dark:bg-dark-card/90"
            style={{ animationDelay: '80ms' }}
          >
            {/* logo tile with soft glow */}
            <BrandMark className="mx-auto block h-16 w-16 rounded-[14px] shadow-[0_10px_30px_-8px_rgba(93,135,255,0.7)]" />

            {/* mobile-only wordmark */}
            <h1 className="mt-5 text-center font-brand text-22 font-extrabold tracking-tight text-dark dark:text-dark-text lg:hidden">
              Mesterplan
            </h1>

            <div className="mt-5 text-center">
              <h2 className="text-xl font-bold tracking-tight text-dark dark:text-dark-text">
                Velkommen til Mesterplan
              </h2>
              <p className="mt-1.5 text-sm text-muted dark:text-dark-muted">
                Log ind eller opret en konto for at komme i gang
              </p>
            </div>

            <div className="mt-6 flex flex-col gap-3">
              <button type="button" onClick={startLogin} className={authButtonClass}>
                Log ind
                <AuthArrowIcon />
              </button>

              <button
                type="button"
                onClick={startSignup}
                className="group flex w-full items-center justify-center gap-2 rounded-xl border border-primary px-6 py-3.5 text-sm font-semibold text-primary transition-all duration-200 hover:bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                Opret konto
                <AuthArrowIcon />
              </button>
            </div>

            <div className="mt-6 flex items-center justify-center gap-1.5 text-xs text-lightgray">
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="5" y="11" width="14" height="10" rx="2" />
                <path d="M8 11V7a4 4 0 1 1 8 0v4" />
              </svg>
              Sikret af RsConnect
            </div>
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
}

// Re-exported so the existing import site (isGuestPath.test.ts) keeps working;
// the definition moved to lib/ because NetClientsProvider needs it too.
export { isGuestPath };

// IDP auth path — used when VITE_IDP_ISSUER_URL is set.
// Must be rendered inside <OidcAuthProvider> (provided by NetAuthProvider in main.tsx).
function IdpGatedApp() {
  const { status, user } = useAuth();
  const { pathname, search } = useLocation();
  // Track whether we transitioned through 'authenticated' so that after logout
  // (status flips to 'unauthenticated' while the browser navigates away) we show
  // a brief spinner instead of the login page flashing.
  const wasAuthenticated = useRef(false);

  useEffect(() => {
    if (status === 'authenticated') wasAuthenticated.current = true;
  }, [status]);

  // A cross-product handshake link (/integrations/connect?from=…&connection=…)
  // arrives before any page of the app has rendered — the login screen replaces
  // the whole tree while signed out. Stash its params here, at the gate, so the
  // connect page can resume even if a detour (login, registration, first-company
  // onboarding) comes back without the query string. The returnUrl carries it in
  // the normal case; this is the belt to that brace.
  useEffect(() => {
    if (pathname.replace(/\/+$/, '') !== '/integrations/connect') return;
    const params = parseConnectParams(search);
    if (params) rememberConnectParams(params, window.sessionStorage);
  }, [pathname, search]);

  if (status === 'initializing') return <LoadingScreen />;

  // Guest surfaces are reached by people who have no Mesterplan account at all: a
  // customer opening the portal link from a notification email, a resident opening
  // the modtagerliste link, someone opening a shared timeplan or KS. Their routes
  // live inside AuthenticatedRoutes, so without this check the auth gate below
  // served them the LOGIN SCREEN and every one of those links was a dead end.
  // Authorisation on these pages is the token in the URL, not a session — so render
  // the same tree (they need its providers) without requiring one.
  if (isGuestPath(pathname)) {
    return (
      <ThemeProvider>
        <AuthenticatedRoutes firebaseUser={null} />
      </ThemeProvider>
    );
  }

  // After logout: status flips to unauthenticated while browser is navigating away.
  if (status === 'unauthenticated' && wasAuthenticated.current) return <LoadingScreen message="Logger ud..." />;
  // Fresh visitor: serve our own login + signup forms (no redirect to rs-connect).
  if (status === 'unauthenticated') return <IdpLoginScreen />;

  if (pathname === '/admin' || pathname.startsWith('/admin/')) {
    return <ThemeProvider><ErrorBoundary><Suspense fallback={<LoadingScreen />}><Routes><Route path="/admin/*" element={<PlatformAdminApp key={user?.id} />} /></Routes></Suspense></ErrorBoundary></ThemeProvider>;
  }

  // Authenticated via IDP. Firebase user is null — CompanyContext handles this gracefully
  // and returns empty companies until the data layer is migrated to the .NET API.
  return (
    <ThemeProvider>
      <AuthenticatedRoutes firebaseUser={null} />
    </ThemeProvider>
  );
}

// Firebase auth path — original flow, used when IDP is not configured.
function FirebaseGatedApp() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) return <LoadingScreen />;

  if (!user) {
    return (
      <ThemeProvider>
        <ErrorBoundary><Suspense fallback={<LoadingScreen />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* Mail 1's link: sign in here (the URL stays), then the accept page renders. */}
          <Route path="/supplier-access/accept" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/auth/action" element={<ResetPassword />} />
          <Route path="/invitations/accept" element={<AcceptInvitation />} />
          <Route path="/quotes/respond" element={<RespondToQuote />} />
          <Route path="/defect/complete" element={<DefectCompletion />} />
          <Route path="/defect/batch" element={<DefectBatchList />} />
          <Route path="/recipients" element={<RecipientsPage />} />
          <Route path="/tidsplan/:token" element={<GuestTimeplan />} />
          <Route path="/ks/:token" element={<GuestKS />} />
          <Route path="/portal" element={<PortalProvider><Suspense fallback={<RouteFallback />}><Outlet /></Suspense></PortalProvider>}>
            <Route index element={<PortalLogin />} />
            <Route path="login" element={<PortalLogin />} />
            <Route element={<PortalProtectedRoute />}>
              <Route path="dashboard" element={<PortalDashboard />} />
              <Route path="conversation/:projectId" element={<PortalConversation />} />
              <Route path="tidsplan/:projectId" element={<PortalTidsplan />} />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        </Suspense></ErrorBoundary>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <AuthenticatedRoutes firebaseUser={user} />
    </ThemeProvider>
  );
}

function App() {
  return idpEnabled ? <IdpGatedApp /> : <FirebaseGatedApp />;
}

export default App;
